import { NavLink } from "react-router-dom";

import {
  Link,
  OverflowText,
  PlaceholderProducts,
} from "@transfr-inc/dashboard-components";
import {
  Product,
  getLearnerTerm,
} from "@transfr-inc/dashboard-components/constants";
import {
  formatDateDigits,
  getProduct,
} from "@transfr-inc/dashboard-components/utils";

import {
  BasicCell,
  Column,
  ColumnOptions,
  ColumnType,
  TableCellListContent,
} from "@transfr-inc/dashboard-components/data-table";

import {
  MULTI_ORG_CHILD,
  MULTI_ORG_PARENT,
} from "@constants/organization-types";
import { createOrganizationLink } from "@utils/navigation";

const ProductsCell = ({ products }) => {
  return (
    products?.length > 0 && (
      <PlaceholderProducts products={products}></PlaceholderProducts>
    )
  );
};

const getParentOrganization = (organization) => {
  if (organization.typeId === MULTI_ORG_CHILD.id) {
    return organization.organizations?.[0];
  }
  return null;
};

export const getOrganizationName = (organization) => {
  if (organization.typeId === MULTI_ORG_CHILD.id) {
    const parentOrg = getParentOrganization(organization) || {};
    return organization.name.startsWith(parentOrg.name)
      ? organization.name
      : `${parentOrg.name ? `${parentOrg.name} - ` : ""} ${
          organization.name
        }`.trim();
  } else {
    return organization.name;
  }
};

export const OrganizationsColumnDefinition = {
  Name: (parent) =>
    new Column(
      "name",
      "Name",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, row) => {
          const breadcrumbLabel = parent ? parent?.name : "All Organizations";
          const link = createOrganizationLink({
            code: row.code,
            search: { from: breadcrumbLabel },
          });
          return (
            <OverflowText text={value}>
              <Link routerComponent={NavLink} to={link}>
                {getOrganizationName(row)}
              </Link>
            </OverflowText>
          );
        },
        sortKeyFunctions: [getOrganizationName],
      }),
    ),
  OrgCode: new Column(
    "code",
    "Client Code",
    ColumnType.BASIC,
    new ColumnOptions({
      sortingEnabled: true,
      overflowEnabled: true,
    }),
  ),
  MaxTrainees: new Column(
    "seats",
    `Max ${getLearnerTerm({ plural: true })}`,
    ColumnType.BASIC,
    new ColumnOptions({
      sortingEnabled: true,
      overflowEnabled: true,
    }),
  ),
  ChildOrgs: new Column(
    "organizations",
    "Child Orgs",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      overflowEnabled: true,
      customFunction: (value, row) => {
        const orgsNames =
          row.typeId === MULTI_ORG_PARENT.id
            ? row.organizations?.map((o) => o.name)
            : [];
        const orgsCount = orgsNames?.length ?? 0;
        return (
          <TableCellListContent
            collection={orgsNames}
            collectionCount={orgsCount}
          />
        );
      },
    }),
  ),
  ExpirationDate: new Column(
    "expiration",
    "Expiration Date",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      overflowEnabled: true,
      customFunction: (value) => {
        const formattedDate = value ? formatDateDigits(value) : undefined;
        return <BasicCell value={formattedDate}></BasicCell>;
      },
    }),
  ),
  Products: new Column(
    "products",
    "Products",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      overflowEnabled: true,
      customFunction: (value) => {
        const isProgramsEnabled =
          process.env.PROGRAMS_ENABLED.toUpperCase() === "TRUE";

        const processedIds = (value ?? [])
          .filter(Boolean)
          .filter((productId) => {
            if (productId === Product.PROG) {
              return isProgramsEnabled;
            }
            return true;
          })
          .sort((a, b) => {
            const prodA = getProduct(a);
            const prodB = getProduct(b);
            if (!prodA || !prodB) return 0;
            return prodA.displayName.localeCompare(prodB.displayName);
          });

        return <ProductsCell products={processedIds}></ProductsCell>;
      },
    }),
  ),
};
